import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MasterService } from 'src/app/core/services/master.service';
import { trackById } from 'src/app/core/utils/track-by';
import {
  AppLiteState,
  toAppLiteStateArray,
} from 'src/app/features/admin-portal/modules/subscription/models/subscription.models';
import { SubscriptionService } from 'src/app/features/admin-portal/modules/subscription/services/subscription.service';
import { UserModel } from 'src/app/features/company-portal/modules/user-management/models/user-management.models';
import { UsersManagementService } from 'src/app/features/company-portal/modules/user-management/services/user-management.service';
import { environment } from '../../../environments/environment';
import { Apps } from '../../enums/enums';
import { normalizeAppName } from '../../models/shared.models';
import { SharedService } from '../../services/shared.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: false,
})
export class HomeComponent {
  trackById = trackById;
  public Apps = Apps;
  @ViewChild('defaultStoreWarning')
  defaultStoreWarning?: ElementRef<HTMLElement>;
  imsUrl: string = environment.imsUrl;
  userInfo: any;
  allApps: Array<AppLiteState> = [];
  userApps: Array<AppLiteState> = [];
  userObj: UserModel = new UserModel();
  tpmUrl: string = environment.tpmUrl;
  listerUrl: string = environment.listerUrl;
  posUrl: string = environment.posUrl;
  donationUrl: string = environment.donationUrl;
  contextMenuPosition = { x: '0px', y: '0px' };
  showContextMenu = false;
  selectedApp: any = null;
  isNotSufficientPermission: boolean = false;
  canShowImsOption: boolean = environment.canShowImsOption;
  isIMSSelected: boolean = false;
  consignmentUrl: string = environment.consignmentUrl;
  showConsignment: boolean = true;

  constructor(
    private userManagementService: UsersManagementService,
    private masterService: MasterService,
    private subscriptionService: SubscriptionService,
    private sharedService: SharedService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.userInfo = this.masterService.getUserDetailFromStorage();
    await this.getAllAppsList();
    this.getUserDetails();
  }

  async getAllAppsList(): Promise<void> {
    try {
      const res = await firstValueFrom(this.sharedService.getApps());
      this.allApps = toAppLiteStateArray(res).map((app) => ({
        ...app,
        name: normalizeAppName(app.id, app.name),
      }));
    } catch (error) {
      console.error('Error fetching apps:', error);
    }
  }

  async getUserDetails(isAfterUpdateCall: boolean = false): Promise<void> {
    const userId = this.userInfo.userid;
    try {
      const res = await firstValueFrom(
        this.userManagementService.getUserLiteById(userId, isAfterUpdateCall),
      );
      this.userObj = res as UserModel;
      const userAppsIds = this.userObj.apps.map((item) => item.id);
      this.userApps = this.allApps.filter((item) => userAppsIds.includes(item.id));
      this.isIMSSelected = userAppsIds.includes(Apps.Inventory);

      // Check permissions for POS application
      this.userManagementService.getUserPermissionsByUserId(userId).subscribe({
        next: (permResult: any) => {
          const permissions = permResult as any[];
          const posAppPermissions = permissions.find(
            (p) => p.appName === 'PointOfSale' || p.appName === 'POS',
          );

          if (posAppPermissions) {
            const allPermissions: string[] = [];
            posAppPermissions.permissionGroups.forEach((group: any) => {
              group.permissions.forEach((perm: any) => {
                allPermissions.push(perm.name);
              });
            });

            // Disable POS app if it only has the PointOfSale:Cashier permission
            if (
              allPermissions.length === 1 &&
              (allPermissions[0] === 'PointOfSale:Cashier' || allPermissions[0] === 'Cashier')
            ) {
              const posApp = this.userApps.find((app) => app.id === Apps.POS);
              if (posApp) {
                posApp.isDisabled = true;
              }
            }
          }
          this.isNotSufficientPermission = this.userApps.length == 0;
        },
        error: () => (this.isNotSufficientPermission = this.userApps.length == 0),
      });
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  }

  redirectToModule(appId: number) {
    switch (appId) {
      case Apps.TPM:
        if (this.userObj.defaultStoreId) {
          const baseUrl = this.tpmUrl.split('?')[0];
          this.tpmUrl = `${baseUrl}?appId=${appId}`;
          window.location.href = this.tpmUrl;
        } else {
          this.defaultStoreWarning?.nativeElement.click();
        }
        break;

      case Apps.POS:
        const posApp = this.userApps.find((app) => app.id === Apps.POS);
        if (posApp?.isDisabled) {
          return;
        }
        window.location.href = this.posUrl;
        break;

      case Apps.Lister:
        window.location.href = this.listerUrl;
        break;

      case Apps.Inventory:
        window.location.href = this.imsUrl;
        break;

      case Apps.Donation:
        window.location.href = this.donationUrl;
        break;

      case Apps.Consignment:
        window.location.href = this.consignmentUrl;
        break;

      default:
        console.error('Invalid module name');
        return;
    }
  }

  onRightClick(event: MouseEvent, item: any) {
    event.preventDefault();
    this.selectedApp = item;

    this.contextMenuPosition.x = `${event.clientX}px`;
    this.contextMenuPosition.y = `${event.clientY}px`;
    this.showContextMenu = true;
  }

  openInNewTab() {
    this.showContextMenu = false;
    if (this.selectedApp) {
      const appId = this.selectedApp.id;
      const url = this.getAppUrl(appId);
      if (appId == Apps.TPM && !this.userObj.defaultStoreId) {
        this.defaultStoreWarning?.nativeElement.click();
      } else {
        window.open(url, '_blank');
      }
    }
  }

  // Create a method to build the correct URL
  getAppUrl(appId: number): string {
    switch (appId) {
      case Apps.TPM:
        const baseUrl = this.tpmUrl.split('?')[0];
        return `${baseUrl}?appId=${appId}`;
      case Apps.POS:
        return this.posUrl;
      case Apps.Lister:
        return this.listerUrl;
      case Apps.Inventory:
        return this.imsUrl;
      case Apps.Donation:
        return this.donationUrl;
      case Apps.Consignment:
        return this.consignmentUrl;
      default:
        return '';
    }
  }

  nevigateToDefaultStoreConfig() {
    this.router.navigate(['/company-portal/user-management/user']);
  }

  // Optional: Close context menu on outside click
  @HostListener('document:click')
  onDocumentClick() {
    this.showContextMenu = false;
  }
  accessIMS() {
    window.location.href = this.imsUrl;
  }
  accessConsignment() {
    window.location.href = this.consignmentUrl;
  }
  accessListerMobile(appId: number) {
    const app = this.userApps.find((a) => a.id === appId);
    if (app?.isDisabled) {
      return;
    }

    if (appId == Apps.Lister) {
      window.location.href = `${this.listerUrl}/product-list-mobile`;
    } else if (appId == Apps.Donation) {
      window.location.href = this.donationUrl;
    }
  }

  toggleSidebar() {
    this.sharedService.toggleSidebar();
  }
}
